import { useEffect, useRef, useState } from 'react';
import './Modal.css';
import PurchaseProductModal from './layouts/PurchaseProductModal';
import PurchaseConfirmationModal from './layouts/PurchaseConfirmationModal';
import ActionStepModal from './layouts/ActionStepModal';
import { AdvisoryContent } from '../../../common/global-types/AdvisoryContent';
import APIUtil from '../../../common/api/APIUtil';

type ModalProps = {
  content: AdvisoryContent;
  viewId: number | undefined;
  setViewId: (step: number | undefined) => void;
  proxyPath: string | null;
};

function Modal({
  content,
  viewId,
  setViewId,
  proxyPath
}: ModalProps) {
  const modalRef = useRef<HTMLDialogElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const [modalMaxWidth, setModalMaxWidth] = useState<number>(1084);

  const view = viewId ? content.contentViews?.find((cv) => cv.contentViewId === viewId) : undefined;

  useEffect(() => {
    if (view && !view?.viewElements.media) {
      setModalMaxWidth(560);
    }
  }, [view]);

  const onModalOpen = () => {
    let timeoutId:NodeJS.Timeout;
    if (modalRef.current) {
      modalRef.current.showModal();

      /* Handle unknown race condition on My Pages start,
      where sometimes background is scrolable on IPhone when modal is open */
      timeoutId = setTimeout(() => {
        if (closeButtonRef.current) {
          document.body.classList.add('advisory-overflow-hidden');

          closeButtonRef.current.focus();
        }
      }, 250);
      return timeoutId;
    }
    return undefined;
  };

  const onModalClose = () => {
    if (modalRef.current) modalRef.current.close();

    document.body.classList.remove('advisory-overflow-hidden');
  };

  const handleKey = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
    }
  };

  useEffect(() => {
    let timeoutId:NodeJS.Timeout | undefined;
    if (viewId) {
      timeoutId = onModalOpen();

      document.addEventListener('keydown', handleKey, false);
    } else {
      onModalClose();
    }
    return () => {
      document.removeEventListener('keydown', handleKey, false);
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [viewId]);

  const handleOtherClick = async (actionIndex: number) => {
    if (!view?.viewElements?.actions) {
      return;
    }    
    const action = view.viewElements.actions[actionIndex];

    if (action.callbackPath) {
      APIUtil.handleCallbackUrl(proxyPath, action.callbackPath);
    }
  }

  if (view === undefined) return null;

  return (
    <dialog
      id="advisory-modal"
      ref={modalRef}
      style={{ maxWidth: `${modalMaxWidth}px` }}
    >
      {view.viewElements.actions && view.viewElements.actions[0] && (
        <button
          ref={closeButtonRef}
          aria-label="close modal-v19"
          className="if button medium tonal modal-v19-close-button"
          id="close-modal-v19"
          type="button"
          onClick={() => {
            handleOtherClick(0);
            setViewId(view.viewElements.actions![0].loadView!);
          }}
        >
          <ids-icon {...{
            [view.viewElements.actions[0].idsIconName!]: true,
            [`${view.viewElements.actions[0].idsIconGroup}-24`]: true
          }}
          />
        </button>
      )}
      <div id="advisory-modal-content">
        {view.viewLayout?.toLowerCase() === 'purchaseproduct' && (
          <PurchaseProductModal
            view={view}
            setViewId={setViewId}
            proxyPath={proxyPath}
            setModalMaxWidth={setModalMaxWidth}
            content={content}
            handleOtherClick={handleOtherClick}
          />
        )}
        {view.viewLayout?.toLowerCase() === 'purchaseconfirmation' && (
          <PurchaseConfirmationModal
            view={view}
            setModalMaxWidth={setModalMaxWidth}
            content={content}
          />
        )}
        {view.viewLayout?.toLocaleLowerCase() === 'actionstep' && (
          <ActionStepModal
            view={view}
            setViewId={setViewId}
            proxyPath={proxyPath}
            content={content}
          />
        )}
      </div>
    </dialog>
  );
}

export default Modal;
